import { z } from "zod";

/**
 * The scalar shapes every other schema is built from. Defined once so that
 * "what is a month?" has exactly one answer on both sides of the wire.
 */

/** ULIDs: 26 characters, Crockford base32, sortable by creation time. */
export const Ulid = z.string().regex(/^[0-7][0-9A-HJKMNP-TV-Z]{25}$/, "Expected an id");

/**
 * An id as it appears on the wire: opaque, url-safe, bounded.
 *
 * New rows get a ULID. The migrated ledger does not — phase 1 minted its ids
 * from a hash, and rewriting 473 primary keys to satisfy a regex would be
 * gratuitous risk on real money. So the wire type is the permissive one and
 * `Ulid` describes only what we generate.
 */
export const Id = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/, "Expected an id");

/** `YYYY-MM-DD`. Calendar-checked, so 2026-02-30 is rejected. */
export const IsoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD")
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, "That date does not exist");

/** `YYYY-MM`. */
export const IsoMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Expected YYYY-MM");

/** ISO 8601 UTC, as SQLite stores it. */
export const IsoTimestamp = z.iso.datetime();

/**
 * Integer pence, signed. Negative is money out.
 *
 * The bound is Number.MAX_SAFE_INTEGER-ish rather than arbitrary: beyond it,
 * addition stops being exact, and a total that silently loses precision is the
 * exact failure this whole convention exists to prevent.
 */
export const Pence = z
  .number()
  .int("Money must be whole pence")
  .min(-9_007_199_254_740, "Amount out of range")
  .max(9_007_199_254_740, "Amount out of range");

/** Currency is GBP-only today; the schema carries it so that stays cheap to change. */
export const Currency = z.enum(["GBP"]);

export const Direction = z.enum(["in", "out"]);

/**
 * A boolean in a query string. `?excluded=true`, `?excluded=1`, and a bare
 * `?excluded` all mean the same thing to a person typing a URL, so they mean
 * the same thing here.
 */
export const BooleanQuery = z
  .union([z.boolean(), z.enum(["true", "false", "1", "0", ""])])
  .transform((value) =>
    typeof value === "boolean" ? value : value === "true" || value === "1" || value === "",
  );

/**
 * Several ids in one query parameter: `?accountIds=acc_joint,acc_card`.
 *
 * Comma-separated rather than the parameter repeated, because nothing else in
 * this API is a list and `parseQuery` takes the last value of a repeated key —
 * and because a filtered screen is a URL one person in the household sends to
 * the other, which this keeps readable.
 *
 * Capped at 20 for the reason `QueryFilters` is: D1 allows 100 bound
 * parameters per statement, and a list this long is already past any
 * household's real number of accounts.
 */
export const IdList = z
  .string()
  .transform((value) => value.split(",").filter((part) => part !== ""))
  .pipe(z.array(Id).max(20));

/** Cursor pagination. Offsets break when a sync inserts rows mid-scroll. */
export const PageQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});

export function page<T extends z.ZodType>(item: T) {
  return z.object({
    data: z.array(item),
    nextCursor: z.string().nullable(),
  });
}

export type Ulid = z.infer<typeof Ulid>;
export type Id = z.infer<typeof Id>;
export type IsoDate = z.infer<typeof IsoDate>;
export type IsoMonth = z.infer<typeof IsoMonth>;
export type IsoTimestamp = z.infer<typeof IsoTimestamp>;
export type Pence = z.infer<typeof Pence>;
export type Currency = z.infer<typeof Currency>;
export type Direction = z.infer<typeof Direction>;
export type PageQuery = z.infer<typeof PageQuery>;
export type BooleanQuery = z.infer<typeof BooleanQuery>;
export type IdList = z.infer<typeof IdList>;
